import { TwilioError } from '@twilio/voice-sdk';
import { ProviderCategoryEnum, useCreateProviderErrorLogMutation } from '@/types/generated/graphql';
import { TWILIO_DEVICE_EVENTS } from '@shared/constants';
import { useEffect } from 'react';
import { debounce, fibonacciSequence } from '@shared/utils';
import { usePhoneStore } from '@modules/phone/store/phone.store';
import { useDeviceStore } from '@modules/phone/store/device.store';
import { TwilioCall } from '@/types/app.types';
import { useProviderLogin } from './use-provider-login';
import { useCaptureClientCall } from './use-capture-client-call';
import { isJwtExpiringSoon } from '@shared/utils/jwt.helper';

export function useDeviceEvents() {
  const [createProviderError] = useCreateProviderErrorLogMutation();
  const [...retrySequence] = fibonacciSequence();
  let currentFibonaaci = [...retrySequence];
  const { captureCallInfo } = useCaptureClientCall();

  const { device, call, setCallState } = useDeviceStore();
  const { openIncomingPhone, setIsIncoming } = usePhoneStore();
  const { refetchProviderLogin } = useProviderLogin();

  function handleTokenWillExpire() {
    refetchProviderLogin();
  }

  function handleIncomingCall(incomingCall: TwilioCall) {
    const memberId = incomingCall.customParameters.get('memberId');
    const sid = incomingCall?.parameters?.CallSid;
    captureCallInfo(sid);

    if (isLiveCall(incomingCall) || isSwitchingCall(incomingCall)) {
      incomingCall.accept();
      setCallState(incomingCall);
      return;
    }

    // ignore the call when user is already in active call
    if (call) return incomingCall?.ignore();
    setCallState(incomingCall);

    if (memberId) {
      openIncomingPhone(true, memberId);
    } else {
      openIncomingPhone();
    }
    setIsIncoming(true);
  }

  function handleTwilioError(error: TwilioError.TwilioError) {
    createProviderError({
      variables: {
        input: {
          error: {
            code: error.code,
            message: error.message,
            stackTrace: JSON.stringify(error),
          },
          category: ProviderCategoryEnum.auth,
        },
      },
    });

    if (error.name === 'AccessTokenExpired') {
      if (currentFibonaaci.length) {
        debounce(refetchProviderLogin, currentFibonaaci[0] * 1000)();
        currentFibonaaci.shift();
      } else {
        retrySequence.reverse();
        currentFibonaaci = retrySequence;
      }
    }
  }

  /**@description register and unregister device events */
  function handleDeviceEvents() {
    if (!device) return;

    device?.on(TWILIO_DEVICE_EVENTS.tokenWillExpire, handleTokenWillExpire);
    device?.on(TWILIO_DEVICE_EVENTS.incoming, handleIncomingCall);
    device?.on(TWILIO_DEVICE_EVENTS.error, handleTwilioError);

    return handleUnregisterDeviceEvents;
  }

  function handleUnregisterDeviceEvents() {
    device?.off(TWILIO_DEVICE_EVENTS.tokenWillExpire, handleTokenWillExpire);
    device?.off(TWILIO_DEVICE_EVENTS.incoming, handleIncomingCall);
    device?.off(TWILIO_DEVICE_EVENTS.error, handleTwilioError);
  }

  function validateDeviceTokenAndRefetch() {
    if (!device || document.hidden) return;
    if (isJwtExpiringSoon(device?.token || '')) refetchProviderLogin();
  }

  function handleVisibilityChange() {
    document.addEventListener('visibilitychange', validateDeviceTokenAndRefetch);
    return () => document.removeEventListener('visibilitychange', validateDeviceTokenAndRefetch);
  }

  useEffect(handleDeviceEvents, [device]);
  useEffect(handleVisibilityChange, [device]);
}

export function isLiveCall(incomingCall: TwilioCall) {
  const isCallWhisper = incomingCall.customParameters.get('superviseStrategy') === 'SuperviseConference';
  const hasSuperviseId = incomingCall.customParameters.get('supervisionId') !== 'undefined';
  return isCallWhisper && hasSuperviseId;
}

export function isSwitchingCall(incomingCall: TwilioCall) {
  const isSwitchedCall = incomingCall.customParameters.get('superviseStrategy') === 'switch';
  const hasSuperviseId = incomingCall.customParameters.get('supervisionId') !== 'undefined';
  return isSwitchedCall && hasSuperviseId;
}

export function isMemberCall(incomingCall: TwilioCall) {
  const isMemberCall = incomingCall.customParameters.get('superviseStrategy') === 'ConnectWithMember';
  return isMemberCall;
}
